import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Card, Typography, Space, Button, Tag } from "antd";
import { GiftOutlined, ThunderboltOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { VoucherStatus } from "../helpers/api";
import { displayCurrency } from "../helpers/functions";
import { isLegacyWebtransferBrand } from "../utils/miniAppBrand";

const { Text } = Typography;

interface VoucherBalanceCardProps {
  onOpenVoucherDeposit: () => void;
  voucher: VoucherStatus | null;
}

export const VoucherBalanceCard: FC<VoucherBalanceCardProps> = ({
  onOpenVoucherDeposit,
  voucher,
}) => {
  const { t, i18n } = useTranslation();

  // Статус ещё не приехал, либо ваучер истёк / выключен — не показываем ничего,
  // чтобы не обещать того, чего сервер не начислит.
  if (!voucher || voucher.status === "expired" || voucher.status === "disabled") {
    return null;
  }

  const used = voucher.status === "used";

  // Дату срока действия форматируем на языке интерфейса, а не жёстко по-русски.
  const validUntilLabel = (() => {
    const d = new Date(voucher.valid_until.replace(" ", "T"));
    return isNaN(d.getTime())
      ? voucher.valid_until
      : d.toLocaleDateString(i18n.language || undefined, { day: "numeric", month: "long" });
  })();

  return (
    <Card
      style={{
        background: "#fff",
        borderRadius: "16px",
        border: used ? "1px solid #f0f0f0" : "1px solid #d9f7be",
        boxShadow: "0 2px 8px rgba(82, 196, 26, 0.05)",
        marginBottom: "12px",
      }}
      bodyStyle={{ padding: "14px 16px" }}
    >
      {/* Ряд переносится, а не сжимается: в WebView кошельков (TronLink, Phantom)
          текст увеличен системным масштабом шрифта, и без minWidth: 0 заголовок
          ломался по буквам, а кнопка выдавливалась за край. */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "1 1 180px", minWidth: 0 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: used ? "#fafafa" : "#f6ffed",
              border: `1px solid ${used ? "#d9d9d9" : "#b7eb8f"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: used ? "#8c8c8c" : "#52c41a",
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            {used ? <CheckCircleOutlined /> : <GiftOutlined />}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ fontSize: "14px", lineHeight: "1.2", overflowWrap: "normal", wordBreak: "normal" }}>
                {t("Multi-Voucher Doubling")}
              </Text>
              <Tag
                color={used ? "default" : "green"}
                style={{ borderRadius: "8px", fontWeight: 700, margin: 0, fontSize: "10px" }}
              >
                {used ? t("USED") : "2X"}
              </Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 2 }}>
              {used && voucher.used
                ? t("Credited +{{amount}} {{currency}}", {
                    amount: voucher.used.bonus_amount,
                    currency: displayCurrency(voucher.used.currency),
                  })
                : isLegacyWebtransferBrand
                ? t("Doubles your top-up (GRAM / USDT) • Until {{date}}", {
                    date: validUntilLabel,
                  })
                : t("Doubles your next top-up • Until {{date}}", {
                    date: validUntilLabel,
                  })}
            </Text>
          </div>
        </div>

        {!used && (
          <Button
            type="primary"
            size="middle"
            icon={<ThunderboltOutlined />}
            onClick={onOpenVoucherDeposit}
            style={{
              borderRadius: "10px",
              fontWeight: 600,
              background: "#52c41a",
              borderColor: "#52c41a",
              boxShadow: "0 2px 6px rgba(82, 196, 26, 0.2)",
              flexShrink: 0,
            }}
          >
            {t("Double")}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default VoucherBalanceCard;
